/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * VALIDATE ALL THE THINGS
 */
import { checkDiff, find, findExplicit } from "../../../common-deprecated/utils";
import Debug from "../../../common-deprecated/Debug";
import CarDataConsolidator from "./classes/CarDataConsolidator";
import type { ItemsType } from "../../../common-deprecated/redux/typeHelpers";
import type { BasketType } from "../types/BasketType";
import type { ExtraType, UpholsteryType, WheelType } from "../../../common-deprecated/types/CommonTypes";

// Heavily based on BasketType
export type ConfigurationType = {
    colour: BasketType["colour"];
    primaryAccentColour: BasketType["primaryAccentColour"] | null;
    secondaryAccentColour: BasketType["primaryAccentColour"];
    upholstery: BasketType["upholstery"];
    wheel: BasketType["wheel"];
    extras: BasketType["extras"];
    packs: BasketType["packs"];
    packExtras: BasketType["packExtras"];
    userSelected: BasketType["userSelected"];
};

export enum ValidateConfigAction {
    Colour = "colour",
    Upholstery = "upholstery",
    AddExtra = "addExtra",
    AddPack = "addPack",
    AddPackExtra = "addPackExtra",
    RemoveExtra = "removeExtra",
    RemovePack = "removePack",
    RemovePackExtra = "removePackExtra",
}

/**
 * Validate colour/upholstery combinations. Handling of availability rules is done in a separate function.
 */
const validateColour = (
    config: ConfigurationType,
    action: ValidateConfigAction,
    items: ItemsType,
    selectedItem: string,
): ConfigurationType => {
    const { colour, primaryAccentColour, packs, upholstery } = config;
    const availableColours = CarDataConsolidator.getColours(items.colours, config as BasketType);
    if (action !== ValidateConfigAction.Colour) {
        const filteredColours = availableColours.filter((col) => col.upholsteries.includes(upholstery.id));
        const firstAvailableColour = availableColours[0];
        if (
            // If we have no filtered colours this could mean we're dealing with a special pack edge case that doesn't need colour availability checking.
            // make sure that this check doesn't happen more than once, hence the check if the config.colour.id matches the to-be-set firstAvailableColour
            // this can happen if there is a pack upholstery still set as part of the config object and there are colours (outside the pack) which are available in combination with this upholstery however are not the first one in the list => "firstAvailableColour"
            // this will than result in the default color "firstAvailableColour" however it will always loop since the first available colour will never be the one matching the to-be-update pack upholstery since this happens later in validateUpholstery
            (filteredColours.length > 0 &&
                !find(colour.id, filteredColours) &&
                config.colour.id !== firstAvailableColour.id) ||
            // If we are dealing with a pack which contains pack colours which do not have upholsteries defined but the pack does have an upholstery
            (CarDataConsolidator.getBasketPackColours(packs).length &&
                CarDataConsolidator.getBasketPackUpholsteries(packs).length &&
                !find(colour.id, availableColours))
        ) {
            return getValidatedConfig(
                { ...config, colour: firstAvailableColour },
                action,
                items,
                selectedItem,
                "validateColour1",
            );
        } else if (action === ValidateConfigAction.Upholstery && upholstery) {
            const packUpholsteryColours = availableColours.filter(
                (col) => upholstery.availableForColours.includes(col.id) || upholstery.availableForColours.length === 0,
            );
            const firstPackUpholsteryColour = packUpholsteryColours[0];
            if (
                packUpholsteryColours.length > 0 &&
                !find(colour.id, filteredColours) &&
                config.colour.id !== firstPackUpholsteryColour.id
            ) {
                return getValidatedConfig(
                    { ...config, colour: firstPackUpholsteryColour },
                    action,
                    items,
                    selectedItem,
                    "validateColour2",
                );
            }
        }
    }

    // Available Primary accent colours can change based on colour as well.
    if (action === ValidateConfigAction.Colour && primaryAccentColour) {
        const extendedPack = packs.find((pack) => !!pack.extended);
        if (extendedPack && extendedPack.extended && extendedPack.extended.accentColours[colour.id]) {
            const accentColours = extendedPack.extended.accentColours[colour.id];
            if (!find(primaryAccentColour.id, accentColours)) {
                const accentColour = accentColours.find((accentCol) => accentCol.default) || accentColours[0];
                return getValidatedConfig(
                    { ...config, primaryAccentColour: accentColour },
                    action,
                    items,
                    selectedItem,
                    "validateColour3",
                );
            }
        }
    }

    // Even though the current basket colour has the same id, it can still be different because a pack colour can have a different price compared to a non-pack version..
    const packColours = CarDataConsolidator.getBasketPackColours(packs);
    const packColour = find(colour.id, packColours);

    // When a packColour is also a default colour the price of the pack colour could differ from the default colour.
    // Update the colour if the price differs.
    if (packColour && packColour.price.discount !== colour.price.discount) {
        return getValidatedConfig({ ...config, colour: packColour }, action, items, selectedItem, "validateColour4");
    }

    const availableColour = find(colour.id, availableColours);
    // When the id doesn't change there is still a chance that the price differs (ex.: when deselecting a pack containing a colour
    // which is also available as normal on the car but with a specific price.
    // Same applies for availability of upholsteries, which can differ between pack colours and regular colours
    if (
        !packColour &&
        availableColour &&
        (colour.price.discount !== availableColour.price.discount ||
            colour.upholsteries.toString() !== availableColour.upholsteries.toString())
    ) {
        return getValidatedConfig(
            { ...config, colour: availableColour },
            action,
            items,
            selectedItem,
            "validateColour5",
        );
    }

    // If there was a pack colour selected which is not present in the default colours, upon deselecting the pack we should reselect this colour
    if (!availableColour) {
        return getValidatedConfig(
            { ...config, colour: availableColours[0] },
            action,
            items,
            selectedItem,
            "validateColour6",
        );
    }

    return config;
};

/**
 * Validate availability.
 * So availability filtering will only remove items because of a new colour or upholstery being selected.
 * Availability will by default mean that the item is unselectable.
 */
const validateAvailability = (
    config: ConfigurationType,
    action: ValidateConfigAction,
    items: ItemsType,
    selectedItem: string,
): ConfigurationType => {
    const { extras, packs, colour, upholstery } = config;

    // Need to check on action as well, in context of equipment upholsteries you can select an extra which is not valid
    // for the current selected upholstery. However the default upholstery for the equipment upholstery only gets updated in the validateEquipmentUpholsteries function.
    if (action !== ValidateConfigAction.AddExtra && action !== ValidateConfigAction.RemoveExtra) {
        const newExtras = extras.filter(
            (extra) => CarDataConsolidator.isAvailable(extra, colour, upholstery).available,
        );
        if (checkDiff(newExtras, extras))
            return getValidatedConfig(
                { ...config, extras: newExtras },
                action,
                items,
                selectedItem,
                "validateAvailability1",
            );
    }

    // Packs.
    // TODO Still needs to be implemented for removing Aygo packs having upholstery dependencies.
    const newPacks = packs.filter((pack) => CarDataConsolidator.isAvailable(pack, colour, upholstery).available);
    if (checkDiff(newPacks, packs))
        return getValidatedConfig({ ...config, packs: newPacks }, action, items, selectedItem, "validateAvailability2");

    return config;
};

/**
 * Validate the colouringMode which checks against the body, primary and/or secondary accent colors
 * @param config
 * @param action
 * @param items
 * @param selectedItem
 * @returns {ConfigurationType}
 */
const validateColourMode = (
    config: ConfigurationType,
    action: ValidateConfigAction,
    items: ItemsType,
    selectedItem: string,
) => {
    const { extras, colour, primaryAccentColour, secondaryAccentColour } = config;
    const newExtras = extras.filter(
        (
            extra, // Also check the colouringmode
        ) =>
            CarDataConsolidator.validatesForColourMode(extra, {
                colour,
                primaryAccentColour,
                secondaryAccentColour,
            }),
    );
    if (checkDiff(newExtras, extras))
        return getValidatedConfig({ ...config, extras: newExtras }, action, items, selectedItem, "validateColourMode1");

    return config;
};

const validateUpholstery = (
    config: ConfigurationType,
    action: ValidateConfigAction,
    items: ItemsType,
    selectedItem: string,
): ConfigurationType => {
    const { packs, upholstery, colour } = config;
    const { upholsteries, equipmentUpholsteries } = items;

    const hasPackUpholsteries = !!CarDataConsolidator.getBasketPackUpholsteries(packs, false, colour.id).length;
    if (
        (upholstery.availableForColours.length === 0 || !upholstery.availableForColours.includes(colour.id)) &&
        !colour.upholsteries.includes(upholstery.id) &&
        (action === ValidateConfigAction.Colour || (action === ValidateConfigAction.AddPack && hasPackUpholsteries))
    ) {
        // Colour/upholstery mismatch, try to find a new upholstery.
        const availableUpholsteries = CarDataConsolidator.getUpholsteries(
            upholsteries,
            equipmentUpholsteries,
            config as BasketType,
        );
        const availablePackUpholsteries = CarDataConsolidator.getBasketPackUpholsteries(
            packs,
            false,
            colour.id,
        ) as UpholsteryType[];
        const newUpholstery = find(colour.upholsteries[0], availableUpholsteries) || availablePackUpholsteries[0];

        if (newUpholstery && newUpholstery.id !== upholstery.id) {
            return getValidatedConfig(
                { ...config, upholstery: newUpholstery as UpholsteryType },
                action,
                items,
                selectedItem,
                "validateUpholstery1",
            );
        }
        // If no new upholstery is found (presumably because the colour.upholsteries can be emtpy, check if the selected colour is coming from a pack
        if (CarDataConsolidator.getBasketPackColours(packs).length) {
            // If there are no pack upholsteries available as well, throw an error! Else just keep the upholstery as is configured through the pack selection.
            if (!hasPackUpholsteries) {
                Debug.error(
                    "No default upholstery found for colour. (probably because it's a pack color or upholstery)",
                    colour,
                );
            }
        }
        // Check if the upholstery is coming from a pack, than ignore the colour availability
        else if (!hasPackUpholsteries) {
            // No new upholstery found, something went wrong in config.
            Debug.error("No default upholstery found for colour.", colour);
        }
    }

    // AccessoryUpholsteries are validated in the validateAccessoryUpholsteries function.
    const availableUpholsteries = CarDataConsolidator.getUpholsteries(upholsteries, null, config as BasketType);
    const availableUpholstery = find(upholstery.id, availableUpholsteries);
    const defaultUpholstery = CarDataConsolidator.getDefaultUpholstery(availableUpholsteries as UpholsteryType[]);

    if (!availableUpholstery) {
        // Current config upholstery is no longer available, set a new one.
        return getValidatedConfig(
            { ...config, upholstery: defaultUpholstery },
            action,
            items,
            selectedItem,
            "validateUpholstery2",
        );
    }
    // Only resolve user selected upholstery logic if it involves a pack selection or colour selection
    if (
        !hasPackUpholsteries &&
        (action === ValidateConfigAction.RemovePack ||
            action === ValidateConfigAction.AddPack ||
            action === ValidateConfigAction.Colour)
    ) {
        // If no user selected entry has been found, revert to the default upholstery!
        if (upholstery.id !== defaultUpholstery.id) {
            if (action === ValidateConfigAction.AddPack) {
                const selectedPack = find(selectedItem, packs);
                if (selectedPack && selectedPack.availability.upholsteries.length > 0) {
                    if (upholstery.id !== selectedPack.availability.upholsteries[0]) {
                        // Take the first available upholstery
                        const packUpholstery = find(selectedPack.availability.upholsteries[0], availableUpholsteries);
                        return getValidatedConfig(
                            { ...config, upholstery: packUpholstery as UpholsteryType },
                            action,
                            items,
                            selectedItem,
                            "validateUpholstery7",
                        );
                    }
                } else {
                    return getValidatedConfig(
                        { ...config, upholstery: defaultUpholstery },
                        action,
                        items,
                        selectedItem,
                        "validateUpholstery3",
                    );
                }
            }
        }
    }
    // Even though the current basket upholstery has the same id, it can still be different because a pack upholstery can have a different price compared to a non-pack version..
    const packUpholsteries = CarDataConsolidator.getBasketPackUpholsteries(packs, true);
    const packUpholstery = find(upholstery.id, packUpholsteries);

    // When a packUpholstery is also a default upholstery the price of the pack upholstery could differ from the default upholstery.
    // Update the upholstery if the price differs. See OR-756.
    if (packUpholstery && packUpholstery.price.discount !== upholstery.price.discount) {
        return getValidatedConfig(
            { ...config, upholstery: packUpholstery as UpholsteryType },
            action,
            items,
            selectedItem,
            "validateUpholstery5",
        );
    }

    // When the id doesn't change there is still a chance that the price differs (ex.: when deselecting a pack containing an upholstery
    // which is also available as normal on the car but with a specific price. See OR-990.
    if (!packUpholstery && availableUpholstery && upholstery.price.discount !== availableUpholstery.price.discount) {
        return getValidatedConfig(
            { ...config, upholstery: availableUpholstery as UpholsteryType },
            action,
            items,
            selectedItem,
            "validateUpholstery6",
        );
    }

    return config;
};

const validateEquipmentUpholsteries = (
    config: ConfigurationType,
    action: ValidateConfigAction,
    items: ItemsType,
    selectedItem: string,
): ConfigurationType => {
    const { extras, upholstery, packExtras, packs } = config;
    const { equipmentUpholsteries, upholsteries } = items;

    if (action === ValidateConfigAction.AddExtra) {
        const newExtra = findExplicit<ExtraType | UpholsteryType>(
            selectedItem,
            (items.extras as (ExtraType | UpholsteryType)[]).concat(items.equipmentUpholsteries),
        );
        // Added objectType check to keep TS happy.
        if (newExtra.objectType === "extra" && newExtra.isUpholstery) {
            // Technically this can return extraTypes but to fix type errors we're assuming its just upholsteries.
            const allUpholsteries = CarDataConsolidator.getUpholsteries(
                items.upholsteries,
                null,
                config as BasketType,
            ) as UpholsteryType[];
            // Filter out the ones which are actually available for the selected upholstery extra
            const availableUpholsteries = newExtra.availability.upholsteries.length
                ? (newExtra.availability.upholsteries
                      .map((uphol) => find(uphol, allUpholsteries))
                      .filter(Boolean) as UpholsteryType[])
                : allUpholsteries;
            if (!availableUpholsteries.length) {
                Debug.error("Could not find available colours for the selected accessory upholstery!", newExtra);
            }
            const defaultUpholstery = CarDataConsolidator.getDefaultUpholstery(availableUpholsteries);
            // Set the default upholstery if necessary
            if (defaultUpholstery.id !== upholstery.id) {
                return getValidatedConfig(
                    { ...config, upholstery: defaultUpholstery },
                    action,
                    items,
                    selectedItem,
                    "validateEquipmentUpholsteries1",
                );
            }

            // Remove other equipmentUpholsteries.
            const filteredExtras = extras.filter((extra) => (extra.isUpholstery ? extra.id === selectedItem : true));
            if (filteredExtras.length !== extras.length) {
                return getValidatedConfig(
                    { ...config, extras: filteredExtras },
                    action,
                    items,
                    selectedItem,
                    "validateEquipmentUpholsteries2",
                );
            }
        }
    }

    const selectedPackExtras = packs.reduce((acc: (ExtraType | WheelType)[], pack) => acc.concat(pack.equipment), []);
    if (action === ValidateConfigAction.AddPackExtra || action === ValidateConfigAction.RemovePackExtra) {
        const newExtra = findExplicit(selectedItem, selectedPackExtras);
        if (newExtra.objectType === "extra" && newExtra.isUpholstery) {
            // Remove other pack equipmentUpholsteries
            const filteredExtras = packExtras.filter((extra) =>
                extra.isUpholstery ? extra.id === selectedItem : true,
            );
            if (filteredExtras.length !== packExtras.length) {
                return getValidatedConfig(
                    { ...config, packExtras: filteredExtras },
                    action,
                    items,
                    selectedItem,
                    "validateEquipmentUpholsteries3",
                );
            }
        }
    }

    if (action === ValidateConfigAction.Upholstery && extras.find((extra) => extra.isUpholstery)) {
        // A normal upholstery is set, remove equipmentUpholsteries.
        const newConfig = { ...config, extras: extras.filter((extra) => !extra.isUpholstery) };
        return getValidatedConfig(newConfig, action, items, selectedItem, "validateEquipmentUpholsteries4");
    }

    // If we get here this means the current upholstery is correct, however we could still have equipmentUpholsteries that need to be set.
    // Get available equipmentUpholsteries and check.
    const availableUpholsteries = CarDataConsolidator.getUpholsteries(
        upholsteries,
        equipmentUpholsteries,
        config as BasketType,
    );
    if (!availableUpholsteries.find((uph) => uph.objectType === "upholstery" || !uph.isUpholstery)) {
        const allExtras = extras.concat(packExtras);
        // All available upholsteries are equipmentUpholsteries, make sure at least one of them is selected.
        const accessoryUphSelected = !!availableUpholsteries.find(
            (uph) => !!allExtras.find((extra) => extra.id === uph.id),
        );
        if (!accessoryUphSelected) {
            const newConfig = { ...config, extras: extras.concat(availableUpholsteries[0] as ExtraType) };
            return getValidatedConfig(newConfig, action, items, selectedItem, "validateEquipmentUpholsteries5");
        }
    }

    return config;
};

const validatePacks = (
    config: ConfigurationType,
    action: ValidateConfigAction,
    items: ItemsType,
    selectedItem: string,
): ConfigurationType => {
    const { packs, packExtras, extras } = config;
    const selectedPack = find(selectedItem, items.packs);

    // If the pack has been added and it contains a selected optionally available extra, make sure the extra is deselected
    if (action === ValidateConfigAction.AddPack && selectedPack) {
        const selectedPacksEquipment = CarDataConsolidator.getPackEquipment([selectedPack]);
        // Here the extras are being filtered by pack extras, ignore accessory upholsteries since these need to be explicitely selected even when defined as a pack equipment item
        const newExtras = extras.filter((extra) => extra.isUpholstery || !find(extra.id, selectedPacksEquipment));
        if (newExtras.length !== extras.length) {
            // The removed extras which are also optional extras in selected packs are added here as pack extras.
            const removedExtras = selectedPack.equipment.filter(
                (extra) => !extra.standard && find(extra.id, extras),
            ) as ExtraType[];

            const newPackExtras = removedExtras.length ? [...config.packExtras, ...removedExtras] : config.packExtras;

            return getValidatedConfig(
                { ...config, extras: newExtras, packExtras: newPackExtras },
                action,
                items,
                selectedItem,
                "validatePacks1",
            );
        }
    }

    // If the pack has been removed, we also need to remove extras which have included this pack.
    const toBeRemovedIncludedByExtras =
        action === ValidateConfigAction.RemovePack && selectedPack
            ? CarDataConsolidator.getExtrasIncludedBy(selectedPack, extras)
            : [];
    if (toBeRemovedIncludedByExtras.length) {
        const newExtras = extras.filter((extra) => {
            return !toBeRemovedIncludedByExtras.find((excl) => extra.id === excl.id);
        });
        if (checkDiff(newExtras, extras)) {
            return getValidatedConfig({ ...config, extras: newExtras }, action, items, selectedItem, "validatePacks2");
        }
    }

    // If the pack has been removed, we also need to remove packs which have included this pack.
    // Make sure that the selectedItem is an actual pack (this also gets triggered by changing other configurables)
    const toBeRemovedIncludedByPacks =
        action === ValidateConfigAction.RemovePack && selectedPack
            ? CarDataConsolidator.getPacksIncludedBy(selectedPack, packs).map((p) => p.id)
            : [];

    const packsRelatedData = packs.reduce(
        (partialConfig, configPack) => {
            // Get the potential conflicting extras in the includes of the current configPack being checked, conflicting packs are already resolved in the basket logic thus these can be ignored
            const conflictingPacks = CarDataConsolidator.getPackConflictsBetweenPacks(configPack, items.packs);
            // Find included packs and include them, only if the configPack is not toBeRemoved!
            const shouldBeRemoved = toBeRemovedIncludedByPacks.includes(configPack.id);
            const newIncludedPacks = shouldBeRemoved
                ? []
                : configPack.includePacks
                      .filter(
                          (includePack) =>
                              !find(includePack, partialConfig.packs) && !find(includePack, conflictingPacks),
                      )
                      .map((includePack) => ({
                          ...findExplicit(includePack, items.packs),
                          userSelected: false,
                      }))
                      .filter((pack) => !pack.standard); // No longer add standard packs to the config.

            // Create a list of packEquipment of the added pack which can be used to resolve equipment rules.
            const newPackEquipment = selectedPack
                ? selectedPack.visualItems.standard.concat(selectedPack.visualItems.optional).map((packEq) => packEq.id)
                : [];

            // Find included equipment of the pack which are not already selected!
            const includedEquipment =
                shouldBeRemoved || action === ValidateConfigAction.RemoveExtra
                    ? []
                    : CarDataConsolidator.getIncludedExtras(configPack, items.extras).filter(
                          (extra) => !partialConfig.extras.find((newExtra) => extra.id === newExtra.id),
                      );

            // Create list of excludeEquipment, don't resolve when action is triggered by adding an extra!
            const excludedEquipment =
                action === ValidateConfigAction.AddExtra
                    ? []
                    : CarDataConsolidator.getExcludedExtras(configPack, partialConfig.extras);

            // Pack exclude rules (between packs, between pack equipment items and equipment items)
            const newPacks = partialConfig.packs.concat(newIncludedPacks).filter((pack) => {
                if (pack.id === selectedItem) return true;
                if (pack.id === configPack.id && shouldBeRemoved) return false;
                if (newPackEquipment.length) {
                    // Filter packs containing pack equipment present in the new pack.
                    const hasSameEquipment = !!pack.visualItems.standard
                        .concat(pack.visualItems.optional)
                        .find((equipment) => newPackEquipment.includes(equipment.id));

                    if (hasSameEquipment) return false;
                }
                // If there is new includeEquipment in the newly selected pack which is excluded by
                // another selected pack, remove the other selected pack
                if (includedEquipment.length && includedEquipment.find((extra) => pack.excluded.includes(extra.id))) {
                    return false;
                }
                // If there is exclude equipment, check in the packs if none of them include it!
                if (excludedEquipment.length && excludedEquipment.find((extra) => pack.included.includes(extra.id))) {
                    return false;
                }
                // Filter packs excluded by the new pack.
                return !configPack.excludePacks.includes(pack.id);
            });

            // exit for now and restart the entire process by calling getValidatedConfig later
            if (checkDiff(newPacks, partialConfig.packs)) return { ...partialConfig, packs: newPacks };

            // Check pack exclude rules towards equipment this only supports "simple" rules, see BasketLogic -> resolveRemoveExtraIncludeExcludeRules for the more advanced ones...
            // To be fine tuned through OR-1513.
            if (excludedEquipment.length || includedEquipment.length) {
                // When removing extras, also add the extras which are included by the being deselected extra
                // TODO improve this and remove the occurence in BasketLogic resolveRemoveExtraIncludeExcludeRules -> move this to validate extra?
                const toBeRemovedExtras = excludedEquipment.reduce((acc, extra) => {
                    const childOption = CarDataConsolidator.getSelectedExtraOption(extra);
                    const includedBy = CarDataConsolidator.getExtrasIncludedBy(
                        extra,
                        partialConfig.extras,
                        childOption,
                    );
                    return acc.concat(includedBy);
                }, excludedEquipment);

                // Compute new extras!
                const newExtras = partialConfig.extras.concat(includedEquipment).filter((extra) => {
                    return !toBeRemovedExtras.find((excl) => extra.id === excl.id);
                });

                // exit for now and restart the entire process by calling getValidatedConfig later
                return { ...partialConfig, extras: newExtras };
            }

            if (configPack.visualItems.equipmentUpholsteries) {
                // If the pack contains an accessoryUpholstery, make sure the accessory upholstery of the pack is selected.
                // This is similar logic as pack upholsteries.
                const accessoryUpholstery = configPack.visualItems.equipmentUpholsteries.find(
                    (uph) => !!partialConfig.extras.find((extra) => extra.id === uph.id),
                );

                if (
                    accessoryUpholstery &&
                    !partialConfig.packExtras.find((packExtra) => packExtra.id === accessoryUpholstery.id)
                ) {
                    return {
                        ...partialConfig,
                        packExtras: partialConfig.packExtras.concat(accessoryUpholstery),
                        extras: partialConfig.extras.filter((extra) => extra.id !== accessoryUpholstery.id),
                    };
                }
            }

            return partialConfig;
        },
        { packs: config.packs, extras: config.extras, packExtras: config.packExtras },
    );

    // restart the process when packs/extras/packExtras were included/excluded changed
    if (
        checkDiff(packsRelatedData.packs, packs) ||
        checkDiff(packsRelatedData.extras, extras) ||
        checkDiff(packsRelatedData.packExtras, packExtras)
    ) {
        return getValidatedConfig({ ...config, ...packsRelatedData }, action, items, selectedItem, "validatePacks3");
    }

    return config;
};

const validatePackExtras = (
    config: ConfigurationType,
    action: ValidateConfigAction,
    items: ItemsType,
    selectedItem: string,
): ConfigurationType => {
    const { packs, packExtras } = config;

    // Remove packExtras if the containing pack is no longer in the configuration.
    const availablePackExtras = packs.reduce(
        (availableExtras: ExtraType[], pack) =>
            availableExtras.concat(pack.visualItems.optional).concat(pack.visualItems.equipmentUpholsteries),
        [],
    );
    const availablePackExtraIds = availablePackExtras.map((packExtra) => packExtra.id);
    const filteredPackExtras = packExtras.filter((packExtra) => availablePackExtraIds.includes(packExtra.id));
    if (filteredPackExtras.length !== packExtras.length) {
        return getValidatedConfig(
            { ...config, packExtras: filteredPackExtras },
            action,
            items,
            selectedItem,
            "validatePackExtras1",
        );
    }

    // Pack extra exclude rules. Only resolved within the context of the containing pack.
    let excludeParsedPackExtras: ExtraType[] = [];
    if (action === ValidateConfigAction.AddPackExtra) {
        const selectedPackExtra = find(selectedItem, availablePackExtras);
        if (selectedPackExtra)
            excludeParsedPackExtras = CarDataConsolidator.getExcludedExtras(selectedPackExtra, availablePackExtras);
    }

    // Pack extra include rules. Only resolved within the context of the containing pack.
    const includeParsedPackExtras = packExtras
        // Only parse include rules for pack having includes.
        .filter((packExtra) => packExtra.included.length)
        // Add non conflicting pack extras included by another pack extra to the packExtras array.
        .reduce((newExtras, packExtra) => {
            // Find the pack of the extra.
            const pack = packs.find((p) => !!find(packExtra.id, p.visualItems.optional));
            if (pack) {
                // Find non conflicting included extras and add them to the config.
                const includedExtras = CarDataConsolidator.getNonConflictingIncludedExtras(
                    packExtra,
                    pack.visualItems.optional,
                    config as BasketType,
                ).filter((includedExtra) => !find(includedExtra.id, newExtras));
                return newExtras.concat(includedExtras);
            }
            return newExtras;
        }, packExtras);

    // Filter out the excluded items!
    let newPackExtras;
    if (excludeParsedPackExtras.length) {
        newPackExtras = includeParsedPackExtras.filter((packExtra) => !find(packExtra.id, excludeParsedPackExtras));
    } else {
        newPackExtras = includeParsedPackExtras;
    }

    if (checkDiff(newPackExtras, packExtras)) {
        return getValidatedConfig(
            { ...config, packExtras: newPackExtras },
            action,
            items,
            selectedItem,
            "validatePackExtras2",
        );
    }

    // Check if the non-user selected packs are still included by other extras. If not, remove => Not implemented for now.
    // This could theoretically be implemented, however compatibility check didn't have userSelect logic.
    // Tt the time of writing build doesn't have packExtra userSelect support as well.
    // Ignore this for now as this is a big edge case anyway.

    return config;
};

/**
 * Method which currently is only used to resolve include/exclude rules towards packs. This should be extended to resolve all non conflicting include/exclude rules!
 * @param config
 * @param action
 * @param items
 * @param selectedItem
 * @returns {ConfigurationType}
 */
const validateExtras = (
    config: ConfigurationType,
    action: ValidateConfigAction,
    items: ItemsType,
    selectedItem: string,
): ConfigurationType => {
    let newPacks = config.extras.reduce((configPacks, extra) => {
        // Check the pack includes
        if (extra.includePacks.length && action === ValidateConfigAction.AddExtra) {
            configPacks = extra.includePacks.reduce((_, id) => {
                // Only add if not already present in the config
                if (!find(id, config.packs)) {
                    const includedPack = find(id, items.packs);
                    // Only add it if it is actually found in the items list!
                    if (includedPack) configPacks.push(includedPack);
                }
                return configPacks;
            }, configPacks);
        }

        // Check the pack excludes
        if (extra.excludePacks.length && action === ValidateConfigAction.AddExtra) {
            configPacks = configPacks.filter((configPack) => !extra.excludePacks.includes(configPack.id));
        }
        return configPacks;
    }, config.packs.concat());

    // Check if the equipment is included by another pack, if so also remove the pack
    if (action === ValidateConfigAction.RemoveExtra) {
        const activeItem = find(selectedItem, items.extras)!;
        const includedBy = CarDataConsolidator.getPacksIncludedBy(activeItem, items.packs);
        if (includedBy.length) {
            newPacks = newPacks.filter((configPack) => !includedBy.find((inclu) => inclu.id === configPack.id));
        }
    }

    if (checkDiff(config.packs, newPacks)) {
        return getValidatedConfig({ ...config, packs: newPacks }, action, items, selectedItem, "validateExtras1");
    }

    return config;
};

const validateWheels = (
    config: ConfigurationType,
    action: ValidateConfigAction,
    items: ItemsType,
    selectedItem: string,
): ConfigurationType => {
    const { wheels, packs } = items;
    const { wheel } = config;

    // Set a new wheel when the current wheel is no longer available.
    const availableWheels = CarDataConsolidator.getWheels(wheels, config as BasketType, packs);
    if (!find(wheel.id, availableWheels)) {
        // Cast to wheeltype although technically according to typing this could be an extra as well?
        return getValidatedConfig(
            { ...config, wheel: availableWheels[0] as WheelType },
            action,
            items,
            selectedItem,
            "validateWheels1",
        );
    }

    // TODO This will _not_ trigger the Aygo wheel popup, need to implement this if this ever is necessary.

    return config;
};

/**
 * VALIDATE ALL THE THINGS
 */
export const getValidatedConfig = (
    config: ConfigurationType,
    action: ValidateConfigAction,
    items: ItemsType,
    selectedItem: string,
    // Variable used to easily identify in which recursive loophole the error happens.
    // This makes it also easy in case the getValidateConfig goes in an endless loop to spot the location
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    debugIndex: string = "init",
): ConfigurationType => {
    // To debug just remove the console.log here
    //console.log("debugIndex: ", debugIndex);
    config = validatePacks(config, action, items, selectedItem);
    config = validatePackExtras(config, action, items, selectedItem);
    config = validateColour(config, action, items, selectedItem);
    config = validateAvailability(config, action, items, selectedItem);
    config = validateColourMode(config, action, items, selectedItem);
    config = validateUpholstery(config, action, items, selectedItem);
    config = validateEquipmentUpholsteries(config, action, items, selectedItem);
    config = validateExtras(config, action, items, selectedItem);
    config = validateWheels(config, action, items, selectedItem);

    return config;
};
